import { Flex, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrencyEntry, SegmentedControl, Toggle } from '@feathr/components';

import { useDonationFormBuilder } from '../../useDonationFormBuilder';
import SuggestedAmounts from './SuggestedAmounts';

import * as styles from './LeftPanelContent.css';

function LeftPanelContentBeta(): JSX.Element {
  const { t } = useTranslation();
  const {
    handleChangeMinAmount,
    minValue,
    allowMonthlyDonations,
    setAllowMonthlyDonations,
    allowDescriptions,
    setAllowDescriptions,
    donationFrequency,
    setDonationFrequency,
  } = useDonationFormBuilder();

  // When monthly donations are disabled while on monthly tab, switch back to one-time
  useEffect(() => {
    if (!allowMonthlyDonations && donationFrequency === 'monthly') {
      setDonationFrequency('one-time');
    }
  }, [allowMonthlyDonations, donationFrequency, setDonationFrequency]);

  function handleMinAmountChange(value: string | number): void {
    handleChangeMinAmount(Number(value));
  }

  function handleToggleMonthly(checked?: boolean): void {
    setAllowMonthlyDonations(checked ?? false);
  }

  function handleToggleDescriptions(checked?: boolean): void {
    setAllowDescriptions(checked ?? false);
  }

  function handleFrequencyChange(value: string): void {
    setDonationFrequency(value as 'one-time' | 'monthly');
  }

  return (
    <Flex className={styles.root} direction={'column'}>
      <Stack gap={'md'}>
        <Toggle
          helpText={t('Donors will be charged each month on the date of their first donation.')}
          label={t('Allow monthly donations')}
          layout={'well'}
          onChange={handleToggleMonthly}
          value={allowMonthlyDonations}
        />

        <CurrencyEntry
          label={t('Minimum amount')}
          onChange={handleMinAmountChange}
          value={minValue}
        />

        {allowMonthlyDonations && (
          <SegmentedControl
            data={[
              { label: t('One-time'), value: 'one-time' },
              { label: t('Monthly'), value: 'monthly' },
            ]}
            fullWidth={true}
            name={'donation-frequency'}
            onChange={handleFrequencyChange}
            value={donationFrequency}
          />
        )}

        <SuggestedAmounts key={donationFrequency} />

        <Toggle
          label={t('Allow descriptions')}
          layout={'well'}
          onChange={handleToggleDescriptions}
          value={allowDescriptions}
        />
      </Stack>
    </Flex>
  );
}

export default observer(LeftPanelContentBeta);
